import { useState } from 'react';
import { ApiError, useResource } from '@limuzin/kit';
import type { TownView } from '@limuzin/passenger';
import { getTowns, setTown } from '../api';
import { useSession } from '../session';
import { Icon } from '../material';
import { EmptyCard, ErrorNote, ScreenTitle, Spinner } from '../widgets';

/**
 * «Ваш город» — asked once, after signing in. A port of `town_screen.dart`.
 *
 * A town decides which address catalogue the picker completes against and which
 * тариф the fare card quotes, so there is nothing useful to show before it is
 * answered. It is changeable afterwards in «Профиль», and a ride never crosses towns
 * (ADR 0005). Only towns that are switched on are offered: one that would refuse
 * every order has no business on the list.
 */
export function TownScreen() {
  return (
    <div className="foot-page">
      <div className="column town-gate">
        <ScreenTitle
          title="Ваш город"
          subtitle="Здесь мы работаем. От города зависят адреса и тариф."
        />
        <div style={{ marginTop: 20 }}>
          <TownList />
        </div>
      </div>
    </div>
  );
}

/**
 * The towns, one tap each: the tap is the choice, and it is saved on the spot — the
 * same list the gate shows and «Мой город» opens in its sheet.
 */
export function TownList({ onChosen }: { onChosen?: () => void }) {
  const { api, me, setMe } = useSession();
  const towns = useResource<TownView[]>(() => getTowns(api), [api]);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const current = me?.passenger?.townId ?? null;

  const choose = async (town: TownView) => {
    setSaving(town.id);
    setError(null);
    try {
      setMe(await setTown(api, town.id));
      onChosen?.();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem : new ApiError('unknown', 'Что-то пошло не так'));
    } finally {
      setSaving(null);
    }
  };

  if (towns.loading) return <Spinner />;
  if (towns.error) return <ErrorNote error={towns.error} />;
  if ((towns.data ?? []).length === 0) {
    return (
      <EmptyCard
        title="Пока ни один город не работает"
        hint="Сервис ещё настраивается. Попробуйте зайти позже."
      />
    );
  }

  return (
    <>
      <div className="stack" style={{ gap: 10 }}>
        {(towns.data ?? []).map((town) => {
          const chosen = current === town.id;
          return (
            <button
              key={town.id}
              type="button"
              className={chosen ? 'town-row on' : 'town-row'}
              disabled={saving !== null}
              aria-pressed={chosen}
              onClick={() => void choose(town)}
            >
              <span className="grow">{town.name}</span>
              {saving === town.id ? (
                <span className="spinner" style={{ width: 16, height: 16, color: 'var(--accent)' }} />
              ) : (
                chosen && <Icon name="check" size={18} style={{ color: 'var(--accent)' }} />
              )}
            </button>
          );
        })}
      </div>
      <ErrorNote error={error} />
    </>
  );
}
